import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import {
  PromoBannerPosition,
  PromoBannerProduct,
  PromoBannerReferenceType,
  PromoBannerSaveRequest,
  PromoProductBannerFormData
} from '@app/model/promo-banner-management.model';
import {
  parsePromoBannerStyle,
  PROMO_BANNER_DEFAULT_STYLE,
  serializePromoBannerStyle
} from '@app/model/promo-banner-style.model';
import { PromoBannerManagementDataService } from '@app/services/promo-banner-management-data.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { environment } from '@app/../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { finalize, switchMap } from 'rxjs/operators';
import { UtilService } from '@app/services/util.service';

@Component({
  selector: 'meu-promo-product-banner-form',
  templateUrl: './promo-product-banner-form.component.html',
  styleUrls: ['./promo-product-banner-form.component.scss']
})
export class PromoProductBannerFormComponent implements OnInit, OnDestroy {
  searchControl = new FormControl('');

  settingsForm = new FormGroup({
    buttonLabel: new FormControl('Xem ngay', Validators.required),
    textColor: new FormControl(PROMO_BANNER_DEFAULT_STYLE.textColor, Validators.required),
    buttonBg: new FormControl(PROMO_BANNER_DEFAULT_STYLE.buttonBg, Validators.required),
    buttonText: new FormControl(PROMO_BANNER_DEFAULT_STYLE.buttonText, Validators.required),
    showTitle: new FormControl(true),
    showDescription: new FormControl(true),
    showButton: new FormControl(true),
    isActive: new FormControl(true)
  });

  products: PromoBannerProduct[] = [];
  selectedProduct: PromoBannerProduct | null = null;

  selectedImageFile: File | null = null;
  imagePreviewUrl = '';

  isSearching = false;
  isSaving = false;

  page = 1;
  pageSize = 10;
  totalProducts = 0;

  private subscriptions = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: PromoProductBannerFormData,
    private dialogRef: MatDialogRef<PromoProductBannerFormComponent>,
    private promoBannerService: PromoBannerManagementDataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.data.banner) {
      const style = parsePromoBannerStyle(this.data.banner.add_info, 'Xem ngay');

      this.settingsForm.patchValue({
        buttonLabel: style.label,
        textColor: style.textColor,
        buttonBg: style.buttonBg,
        buttonText: style.buttonText,
        showTitle: style.showTitle,
        showDescription: style.showDescription,
        showButton: style.showButton,
        isActive: this.data.banner.is_active
      });

      this.imagePreviewUrl = this.resolveImageUrl(this.data.banner.image);

      if (this.data.banner.product) {
        this.selectedProduct = this.data.banner.product;
      } else if (this.data.banner.reference_id) {
        this.selectedProduct = {
          id: this.data.banner.reference_id,
          name: this.data.banner.title,
          product_code: this.data.banner.description
        };
      }
    }

    this.loadProducts('');

    const searchSubscription = this.searchControl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((keyword: string) => {
        this.page = 1;
        this.loadProducts(keyword || '');
      });

    this.subscriptions.add(searchSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get modalTitle(): string {
    return this.data.banner ? 'Chỉnh sửa Banner sản phẩm' : 'Thiết lập Banner sản phẩm';
  }

  selectProduct(product: PromoBannerProduct): void {
    this.selectedProduct = product;
  }

  isSelected(product: PromoBannerProduct): boolean {
    return !!this.selectedProduct && this.selectedProduct.id === product.id;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalProducts / this.pageSize));
  }

  previousPage(): void {
    if (this.page <= 1 || this.isSearching) {
      return;
    }

    this.page--;

    this.loadProducts(this.searchControl.value || '');
  }

  nextPage(): void {
    if (this.page >= this.totalPages || this.isSearching) {
      return;
    }

    this.page++;

    this.loadProducts(this.searchControl.value || '');
  }

  getProductImageUrl(product: PromoBannerProduct): string | null {
    return this.resolveImageUrl(product.default_image) || null;
  }

  onSelectedImage(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    if (file.type.indexOf('image/') !== 0) {
      input.value = '';
      this.toastr.error('Vui lòng chọn một file hình ảnh');
      return;
    }

    this.selectedImageFile = file;

    const reader = new FileReader();

    reader.onload = () => {
      this.imagePreviewUrl = reader.result as string;
    };

    reader.readAsDataURL(file);
  }

  clearImage(): void {
    this.selectedImageFile = null;
    this.imagePreviewUrl = this.data.banner
      ? this.resolveImageUrl(this.data.banner.image)
      : '';
  }

  resetColors(): void {
    this.settingsForm.patchValue({
      textColor: PROMO_BANNER_DEFAULT_STYLE.textColor,
      buttonBg: PROMO_BANNER_DEFAULT_STYLE.buttonBg,
      buttonText: PROMO_BANNER_DEFAULT_STYLE.buttonText
    });
  }

  get previewLink(): string {
    return this.selectedProduct ? this.buildProductLink(this.selectedProduct) : '';
  }

  loadProducts(keyword: string): void {
    if (this.isSearching) {
      return;
    }

    this.isSearching = true;

    const subscription = this.promoBannerService
      .searchProducts(keyword, this.page, this.pageSize)
      .subscribe(
        (response) => {
          this.products = response.collection || [];

          this.totalProducts = response.total || 0;

          this.isSearching = false;
        },
        () => {
          this.products = [];
          this.totalProducts = 0;
          this.isSearching = false;
        }
      );

    this.subscriptions.add(subscription);
  }

  closeModal(): void {
    if (this.isSaving) {
      return;
    }

    this.dialogRef.close();
  }

  saveBanner(): void {
    this.settingsForm.markAllAsTouched();

    if (this.settingsForm.invalid) {
      this.toastr.error('Vui lòng nhập nội dung nút');
      return;
    }

    if (!this.selectedProduct) {
      this.toastr.error('Vui lòng chọn một sản phẩm');
      return;
    }

    if (!this.data.banner && !this.selectedImageFile) {
      this.toastr.error('Vui lòng chọn hình ảnh nền cho banner');
      return;
    }

    const formValue = this.settingsForm.value;
    const product = this.selectedProduct;

    const productCode = product.product_code || product.sapatco_code || product.sku || '';

    const request: PromoBannerSaveRequest = {
      slug: 'promo-product-' + product.id,
      link: this.buildProductLink(product),
      position: PromoBannerPosition.Product,
      sort_order: 2,
      add_info: serializePromoBannerStyle({
        label: formValue.buttonLabel,
        textColor: formValue.textColor,
        buttonBg: formValue.buttonBg,
        buttonText: formValue.buttonText,
        showTitle: formValue.showTitle !== false,
        showDescription: formValue.showDescription !== false,
        showButton: formValue.showButton !== false
      }),
      banner_group_id: this.data.groupId,
      reference_type: PromoBannerReferenceType.Product,
      reference_id: product.id,
      title: product.name,
      description: productCode,
      is_active: formValue.isActive === true,
      start_date: null,
      end_date: null
    };

    const saveRequest = (this.data.banner
      ? this.promoBannerService.updateBanner(this.data.banner.id, request)
      : this.promoBannerService.createBanner(request)
    ).pipe(
      switchMap((savedBanner) => {
        if (!this.selectedImageFile) {
          return of(savedBanner);
        }

        return this.promoBannerService.uploadBannerImage(
          savedBanner.id,
          this.selectedImageFile
        );
      })
    );

    this.isSaving = true;

    saveRequest
      .pipe(
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(
        () => {
          const message = this.data.banner
            ? 'Cập nhật banner sản phẩm thành công'
            : 'Tạo banner sản phẩm thành công';

          this.toastr.success(message);

          this.dialogRef.close({
            saved: true
          });
        },
        (error) => {
          console.error('Cannot save product banner', error);

          const message =
            error && error.error && error.error.message
              ? error.error.message
              : 'Không thể lưu banner sản phẩm';

          this.toastr.error(message);
        }
      );
  }

  private buildProductLink(product: PromoBannerProduct): string {
    const slug = product.slug || UtilService.slugify(product.name);

    return '/product/' + product.id + '/' + slug + '/details';
  }

  private resolveImageUrl(image: string | null | undefined): string {
    if (!image) {
      return '';
    }

    if (
      image.indexOf('http://') === 0 ||
      image.indexOf('https://') === 0 ||
      image.indexOf('data:') === 0
    ) {
      return image;
    }

    return environment.backendhost.replace(/\/$/, '') + '/' + image.replace(/^\//, '');
  }
}
